/**
 * Normalize a category label/id for comparison.
 */
export const slugifyCategoryKey = (value) => String(value || '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/\s+/g, '-')
  .replace(/[^a-z0-9-]/g, '')
  .replace(/^-+|-+$/g, '');

const CATEGORY_ALIAS_GROUPS = [
  ['appetizers', 'entrees', 'entres', 'entree', 'entree'],
  ['mains', 'main', 'main-course', 'main-courses', 'plats-principaux', 'plats-principal'],
  ['desserts', 'dessert'],
  ['beverages', 'beverage', 'drinks', 'drink', 'boissons', 'boisson'],
];

const getAliasGroup = (value) => {
  const key = slugifyCategoryKey(value);
  if (!key) return null;
  return CATEGORY_ALIAS_GROUPS.find((group) => group.some((alias) => slugifyCategoryKey(alias) === key)) || null;
};

/**
 * Returns true when a dish category value belongs to the given category object.
 */
export const dishMatchesCategory = (dishCategory, category) => {
  if (!category || category?.id === 'all') return true;

  const dishKey = slugifyCategoryKey(dishCategory);
  const categoryKeys = [
    slugifyCategoryKey(category?.id),
    slugifyCategoryKey(category?.name),
  ].filter(Boolean);

  if (categoryKeys.includes(dishKey)) return true;

  const dishGroup = getAliasGroup(dishCategory);
  if (!dishGroup) return false;

  return categoryKeys.some((categoryKey) => dishGroup.some((alias) => slugifyCategoryKey(alias) === categoryKey));
};

/**
 * Map a dish category value to the canonical category id from the admin list.
 */
export const normalizeDishCategory = (dishCategory, categories = []) => {
  const match = categories?.find((category) => dishMatchesCategory(dishCategory, category));
  return match?.id || dishCategory;
};

/**
 * Calculate category counts from dishes array
 * @param {Array} categories - Array of category objects
 * @param {Array} dishes - Array of dish objects
 * @returns {Array} Categories with updated counts
 */
export const calculateCategoryCounts = (categories, dishes) => {
  return categories?.map(category => ({
    ...category,
    count: category?.id === 'all' 
      ? dishes?.length 
      : dishes?.filter(dish => dishMatchesCategory(dish?.category, category))?.length
  }));
};

/**
 * Group dishes by category
 * @param {Array} dishes - Array of dish objects
 * @param {Array} categories - Array of category objects
 * @param {string} activeCategory - Currently selected category
 * @returns {Object} Dishes grouped by category
 */
export const groupDishesByCategory = (dishes, categories, activeCategory) => {
  const grouped = {};
  
  if (activeCategory === 'all') {
    categories?.forEach(category => {
      if (category?.id !== 'all') {
        grouped[category.id] = dishes?.filter(dish => dishMatchesCategory(dish?.category, category));
      }
    });
  } else {
    grouped[activeCategory] = dishes;
  }

  return grouped;
};

/**
 * Format price with currency symbol
 * @param {number} price - Price value
 * @param {string} currency - Currency code (default: 'CAD')
 * @returns {string} Formatted price string
 */
export const formatPrice = (price, currency = 'CAD') => {
  return new Intl.NumberFormat('fr-CA', {
    style: 'currency',
    currency: currency
  })?.format(price);
};

/**
 * Get spice level label
 * @param {number} level - Spice level (0-5)
 * @returns {string} Spice level label
 */
export const getSpiceLevelLabel = (level) => {
  const labels = {
    0: 'Doux',
    1: 'Légèrement épicé',
    2: 'Moyennement épicé',
    3: 'Épicé',
    4: 'Très épicé',
    5: 'Extrêmement épicé'
  };
  return labels?.[level] || 'Non spécifié';
};